import { Dispatch, FC, SetStateAction } from 'react';
import { IYupSchema, FormTempValues, yupSchemas, STEPS, STEPS_LABELS, STEPS_ICONS } from '../../consts';
import { useFormState, useMultistepApi, Multistep, ArrayField } from 'informed';
import Navigation from '../Navigation';
import Subtitle from '../Subtitle';
import { FacilityStatus, IDropdownOption, Nullable } from '@common/interfaces';
import { EditIcon, TrashIcon, UserIcon } from '@assets/svg';
import { format } from 'date-fns';
import InformedField from '../InformedField';
import { Button, TextField } from '@components';
import css from './styles.module.scss';

interface IReviewStep {
  setYupSchema: Dispatch<SetStateAction<IYupSchema>>;
  isNew: boolean;
}

interface IReviewRow {
  label: string;
  value?: string | number | Array<string>;
}

const optLabel = (opt?: Nullable<IDropdownOption>) => opt?.label || '-';

const optList = (opts?: Array<IDropdownOption | null>) =>
  opts && opts.length ? opts.filter((user) => user !== null).map((o) => o!.label) : undefined;

const dateValue = (d?: Nullable<Date>) => (d ? format(d, 'HH:mm') : '-');

const ALL_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ReviewRow: FC<IReviewRow> = ({ label, value }) => {
  return (
    <div className={css.row}>
      <div className={css.label}>{label}</div>
      {Array.isArray(value) ? (
        <ul className={css.list}>
          {value.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      ) : (
        <div className={css.value}>{value || '-'}</div>
      )}
    </div>
  );
};

const ReviewStep: FC<IReviewStep> = ({ setYupSchema, isNew }) => {
  const formState = useFormState();
  const { setCurrent } = useMultistepApi();

  const { details, asset, rules, time, users } = formState.values as unknown as FormTempValues;

  const onClick = (step: string) => {
    setCurrent(step);
    setYupSchema(yupSchemas[step]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Multistep.Step step={STEPS.REVIEW}>
      <Subtitle text='Review facility data' icon={STEPS_ICONS.REVIEW} />
      <div className={css.section}>
        <div className={css.title}>
          {STEPS_ICONS.DETAILS}
          {STEPS_LABELS.DETAILS}
          <button className={css.titleButton} onClick={() => onClick(STEPS.DETAILS)}>
            Edit <EditIcon />
          </button>
        </div>
        {details ? (
          <>
            <ReviewRow label='Name' value={details.name} />
            <ReviewRow label='Status' value={details.is_active ? FacilityStatus.ACTIVE : FacilityStatus.ARCHIVED} />
            <ReviewRow label='Country' value={optLabel(details.country)} />
            <ReviewRow label='City' value={optLabel(details.city_name)} />
            <ReviewRow label='Email' value={details.email} />
            <ReviewRow label='Depot' value={optLabel(details.depot_number)} />
            <ReviewRow label='Latitude' value={details.latitude} />
            <ReviewRow label='Longitude' value={details.longitude} />
            <ReviewRow label='Address' value={details.address} />
            <ReviewRow label='Images' value={details.image?.length ? details.image.map((f) => f.name) : undefined} />
          </>
        ) : null}
      </div>
      <div className={css.section}>
        <div className={css.title}>
          {STEPS_ICONS.RULES}
          {STEPS_LABELS.RULES}
          <button className={css.titleButton} onClick={() => onClick(STEPS.RULES)}>
            Edit <EditIcon />
          </button>
        </div>
        {rules ? (
          <>
            <ReviewRow label='Global rule' value={optLabel(rules.global)} />
            <ReviewRow label='Agencies' value={optList(rules.agencies)} />
          </>
        ) : null}
      </div>
      <div className={css.section}>
        <div className={css.title}>
          {STEPS_ICONS.TIME}
          {STEPS_LABELS.TIME}
          <button className={css.titleButton} onClick={() => onClick(STEPS.TIME)}>
            Edit <EditIcon />
          </button>
        </div>
        {time
          ? ALL_WEEKDAYS.map((day) => {
              const isActive = time.weekdays?.some((w) => w.value === day.toLowerCase());
              if (!isActive) {
                return <ReviewRow key={day} label={day} value='-' />;
              }
              return (
                <ReviewRow
                  key={day}
                  label={day}
                  value={`${dateValue(time.start_time)} - ${dateValue(time.end_time)}`}
                />
              );
            })
          : null}
      </div>
      <div className={css.section}>
        <div className={css.title}>
          {STEPS_ICONS.ASSET}
          {STEPS_LABELS.ASSET}
          <button className={css.titleButton} onClick={() => onClick(STEPS.ASSET)}>
            Edit <EditIcon />
          </button>
        </div>
        {asset?.services?.length
          ? asset.services.map(({ type_of_asset, type_of_repairs }, idx) => (
              <ReviewRow key={idx} label={type_of_asset?.label || ''} value={optList(type_of_repairs)} />
            ))
          : null}
      </div>
      <div className={css.section}>
        <div className={css.title}>
          {STEPS_ICONS.USERS}
          {STEPS_LABELS.USERS}
          <button className={css.titleButton} onClick={() => onClick(STEPS.USERS)}>
            Edit <EditIcon />
          </button>
        </div>
        {users ? (
          <>
            <ReviewRow label='Focal points' value={optList(users.focal_points)} />
          </>
        ) : null}
      </div>
      {isNew ? (
        <div className={css.section}>
          <div className={css.title}>
            <UserIcon />
            Notify users
          </div>
          <InformedField
            label='Notify users'
            description='Please add the email addresses that need to be notified about the Workshop Facility creation'
          >
            <ArrayField name='notify_users'>
              {({ add }) => (
                <>
                  <ArrayField.Items>
                    {({ remove, name }) => {
                      return (
                        <div key={name} className={css.userRow}>
                          <TextField name={name} variant='dark' placeholder='Enter user email' />
                          <Button iconL={<TrashIcon />} variant='icon' onClick={remove} />
                        </div>
                      );
                    }}
                  </ArrayField.Items>
                  <Button type='button' variant='text' text='Add user for notification +' onClick={() => add()} />
                </>
              )}
            </ArrayField>
          </InformedField>
        </div>
      ) : null}
      <Navigation isNew={isNew} setYupSchema={setYupSchema} />
    </Multistep.Step>
  );
};

export default ReviewStep;
